import { Box, Checkbox, HStack, Popover, Portal, Text } from "@chakra-ui/react"
import { useState } from "react"

// Same palette as EPC_COLORS in pilot/pages/PilotDashboard.tsx and EPC_SCALE in
// home-detail/EpcRatingChart.tsx.
const EPC_COLORS: Record<string, string> = {
  A: "#00a65a",
  B: "#4cb848",
  C: "#c6d21f",
  D: "#f5b036",
  E: "#f28b2c",
  F: "#e15326",
  G: "#c32026",
}

export const EPC_BANDS = ["A", "B", "C", "D", "E", "F", "G"]

/** Sentinel for homes with no EPC assessment, selectable alongside the bands. */
export const NO_EPC = "none"

export const EPC_FILTER_OPTIONS = [...EPC_BANDS, NO_EPC]

const optionLabel = (option: string) => (option === NO_EPC ? "No EPC data" : `EPC ${option}`)

/** Bands named individually on the closed trigger before the tail collapses to "+n". */
const MAX_NAMED_BANDS = 4

// useLocalStorage stringifies with String(value), so an array round-trips to "A,B"
// rather than a real array. Persist the joined form and rehydrate through these.
export const serialiseBands = (bands: string[]) => bands.join(",")

export const parseBands = (raw: string) =>
  raw
    .split(",")
    .map((b) => b.trim())
    .filter((b) => EPC_FILTER_OPTIONS.includes(b))

const Swatch = ({ option }: { option: string }) =>
  option === NO_EPC ? (
    <Box w="10px" h="10px" flexShrink={0} borderRadius="2px" border="1px solid" borderColor="gray.400" bg="gray.100" />
  ) : (
    <Box w="10px" h="10px" flexShrink={0} borderRadius="2px" bg={EPC_COLORS[option]} />
  )

interface EpcBandFilterProps {
  value: string[]
  onChange: (value: string[]) => void
  /** Homes per option. Options with no entry render without a count. */
  counts?: Record<string, number>
}

export function EpcBandFilter({ value, onChange, counts }: EpcBandFilterProps) {
  const [open, setOpen] = useState(false)
  const selected = new Set(value)

  // Nothing selected and everything selected both mean "no filter", so they read the same.
  const isUnfiltered = selected.size === 0 || selected.size === EPC_FILTER_OPTIONS.length
  const pickedBands = EPC_BANDS.filter((b) => selected.has(b))
  const hasNoEpc = selected.has(NO_EPC)

  // Name the picked bands ("EPC A, B, C") rather than counting them; past MAX_NAMED_BANDS
  // the letters stop fitting the control, so the tail collapses to "+n".
  const namedBands = pickedBands.slice(0, MAX_NAMED_BANDS)
  const hiddenBands = pickedBands.length - namedBands.length

  const triggerLabel = isUnfiltered
    ? "All EPC ratings"
    : [
        namedBands.length && `EPC ${namedBands.join(", ")}${hiddenBands ? ` +${hiddenBands}` : ""}`,
        hasNoEpc && "No EPC",
      ]
        .filter(Boolean)
        .join(" · ")

  const toggle = (option: string) => {
    const next = new Set(selected)
    if (next.has(option)) {
      next.delete(option)
    } else {
      next.add(option)
    }
    onChange(EPC_FILTER_OPTIONS.filter((o) => next.has(o)))
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(d: { open: boolean }) => setOpen(d.open)}
      positioning={{ placement: "bottom-start" }}
    >
      <Popover.Trigger asChild>
        <Box
          as="button"
          type="button"
          aria-label="Filter by EPC rating"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          h="40px"
          px="10px"
          minW="200px"
          maxW="300px"
          border="1px solid #E2E8F0"
          borderRadius="8px"
          fontSize="0.875rem"
          color={isUnfiltered ? "#718096" : "#4A5568"}
          bg="white"
          cursor="pointer"
          textAlign="left"
          transition="border-color 0.15s"
          _hover={{ borderColor: "#CBD5E0" }}
        >
          <Text truncate>{triggerLabel}</Text>
          <Box as="span" color="#718096" flexShrink={0} lineHeight={0}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </Box>
        </Box>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            w="250px"
            zIndex={1100}
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="0 8px 24px rgba(0,0,0,0.12)"
          >
            <Popover.Body p={2}>
              {EPC_FILTER_OPTIONS.map((option) => {
                const count = counts?.[option]
                return (
                  <Box key={option}>
                    {option === NO_EPC && <Box h="1px" bg="gray.200" my={1.5} />}
                    <Checkbox.Root
                      checked={selected.has(option)}
                      onCheckedChange={() => toggle(option)}
                      colorPalette="green"
                      size="sm"
                      w="full"
                      px={2}
                      py={1.5}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label flex="1" minW={0} fontWeight="normal">
                        <HStack gap={2} minW={0}>
                          <Swatch option={option} />
                          <Text
                            fontSize="0.875rem"
                            truncate
                            color={option === NO_EPC ? "gray.600" : "gray.700"}
                          >
                            {optionLabel(option)}
                          </Text>
                        </HStack>
                      </Checkbox.Label>
                      {count !== undefined && (
                        <Text fontSize="xs" color="gray.500" flexShrink={0}>
                          {count}
                        </Text>
                      )}
                    </Checkbox.Root>
                  </Box>
                )
              })}
              <Box h="1px" bg="gray.200" my={1.5} />
              <HStack justify="space-between" px={2}>
                <Box
                  as="button"
                  type="button"
                  fontSize="xs"
                  color="green.700"
                  fontWeight="medium"
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                  onClick={() => onChange([...EPC_FILTER_OPTIONS])}
                >
                  Select all
                </Box>
                <Box
                  as="button"
                  type="button"
                  fontSize="xs"
                  color="gray.500"
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                  onClick={() => onChange([])}
                >
                  Clear
                </Box>
              </HStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export default EpcBandFilter
